import { mountAnteroomShell } from "../app/shell";
import { WorldScene } from "../experience/world";
import { resolveQuality } from "../systems/quality";
import { createScrollExperience, type ScrollHandle } from "../systems/scroll";
import { KnowledgePanel } from "../ui/knowledge";
import { mountHotspots } from "../ui/hotspots";
import { SITE } from "../brand/config";
import { track } from "../systems/analytics";
import { mountLeafFractal, type LeafFractalHandle } from "../ui/leafFractal";

export type AnteroomHandle = {
  destroy: () => void;
};

export function mountAnteroom(app: HTMLElement, panel: KnowledgePanel): AnteroomHandle {
  const shell = mountAnteroomShell(app);
  const quality = resolveQuality();
  shell.tierPill.textContent = `Tier ${quality.tier}${quality.reducedMotion ? " · reduced motion" : ""}`;

  let leaf: LeafFractalHandle | null = null;
  try {
    leaf = mountLeafFractal(shell.leafCanvas, { intensity: "subtle" });
  } catch {
    shell.leafCanvas.style.display = "none";
  }

  let load = 0;
  const loadTimer = window.setInterval(() => {
    load = Math.min(100, load + (quality.tier === "C" ? 20 : 8));
    shell.loaderPct.textContent = `${String(load).padStart(2, "0")}%`;
    if (load >= 100) {
      window.clearInterval(loadTimer);
      shell.loader.classList.add("is-done");
    }
  }, 40);

  let world: WorldScene | null = null;
  try {
    world = new WorldScene(shell.canvas, quality);
    world.setProgress(0);
    world.start();
  } catch (err) {
    console.error("WebGL failed", err);
    shell.canvas.style.display = "none";
    shell.brandLine.textContent = SITE.promise;
  }

  const hotspots = mountHotspots(shell.hud, panel);

  let scroll: ScrollHandle | null = null;

  const applyProgress = (p: number) => {
    world?.setProgress(p);
    const state = world?.director.sample(p);
    if (state) {
      shell.chapterLabel.textContent = state.label;
      shell.chapterLearn.textContent = state.act.learns;
      if (state.line) shell.brandLine.textContent = state.line;
      hotspots.update(p);
      hotspots.updateForChapter(state);
    }
    shell.progressEl.textContent = String(Math.round(p * 100)).padStart(3, "0");
    shell.enter.classList.toggle("is-solid", p >= 0.78 || p < 0.12);
  };

  scroll = createScrollExperience(shell.track, quality, applyProgress);
  applyProgress(0);

  shell.enter.addEventListener("click", () =>
    track("enter_click", { at: Math.round(scroll?.progress ?? 0) }),
  );

  document.title = `${SITE.name} | ${SITE.tagline}`;
  track("anteroom_view");

  return {
    destroy: () => {
      window.clearInterval(loadTimer);
      scroll?.destroy();
      world?.dispose();
      leaf?.destroy();
      panel.close();
      hotspots.layer.remove();
      app.innerHTML = "";
    },
  };
}
